/* eslint-disable no-unused-vars */
// eslint-disable react/no-danger
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {gql} from 'apollo-boost';
import {Query} from 'react-apollo';
import {fieldObjectSectionStyle} from "../../helpers/graphQLShortcuts";
import {fieldObjectSectionStyleCss} from "../../helpers/createStyles";
import {filterHeadline, filterHTML, filterUrl, filterResource} from "../../helpers/filters";
import Paragraph from "../Paragraph";
import {trackEventByUrl} from "../../helpers/events";
import ReactVideoModal from "../ReactVideoModal/ReactVideoModal";

interface Data {
    paragraphById: {
        fieldTxtHeadline: string;
        fieldTxtSubTitle: string;
        fieldVarSubheadAboveHeadline: string;
        fieldVarIsVideoLink: boolean;
        fieldVarVideoLink: Array<{
            title: string;
            uri: string;
        }>;
        fieldHtmlBody: Array<{
            value: string;
            format: string;
            processed: string;
        }>;
        fieldObjectSplitCta: Array<{
            targetRevisionId: string
        }>;
        fieldObjectSplitStyle: Array<{entity: Array}>;
        fieldImageAboveContent: boolean;
        fieldVideoImage: Array<{
            entity: Array<{
                fieldMediaImage: Array<{
                    width: number;
                    height: number;
                    url: string;
                    targetId: string;
                    alt: string;
                    title: string;
                }>;
            }>;
        }>;
        fieldReverseVideoLinkText: boolean;
    }
}

class InnerSplitItemQuery extends Query<Data, {id: string}> {}

const GET_COMPONENT = gql`
query paragraphById($id: String!) {
  paragraphById:paragraphRevisionById(id: $id) {
    ... on Paragraph {
      ... on Paragraph {
        ... on ParagraphInnerSplitContentItem {
          fieldObjectSplitCta {
            targetRevisionId
          }
          fieldObjectSplitStyle {
              ${fieldObjectSectionStyle}
          }
          fieldTxtHeadline
          fieldTxtSubTitle
          fieldVarSubheadAboveHeadline
          fieldVarIsVideoLink
          fieldVarVideoLink {
            uri
            title
          }
          fieldHtmlBody {
            value
            format
            processed
          }
          fieldImageAboveContent
          fieldVideoImage {
              entity {
                ... on MediaImage {
                  fieldMediaImage {
                    width
                    height
                    url
                    targetId
                    alt
                    title
                  }
                }
              }
          }
          fieldReverseVideoLinkText
        }
      }
    }
  }
}
`;

class InnerSplitItem extends Component {
    static propTypes = {
        id: PropTypes.string.isRequired,
        markComponentLoadedAs: PropTypes.func.isRequired,
        handleLoadedComponents: PropTypes.func.isRequired
    };

    state = {
        modalOpen: false
    };

    hasRendered = () => {
        const accordions = document.querySelectorAll('.js-accordion');
        if (!accordions || !accordions.length) return;

        accordions.forEach(accordion => {
            if (accordion.getAttribute('data-accordion-prefix-classes') === 'minimalist') return;

            accordion.setAttribute('data-accordion-prefix-classes', 'minimalist');
            // Add Header class since Drupal strips it
            accordion.querySelectorAll(':scope > h1,h2,h3,h4,h5,h6').forEach(headline => {
                headline.classList.add('js-accordion__header')
            });

            // eslint-disable-next-line no-undef
            van11yAccessibleAccordionAria().attach();
        });

    };

    openModal = (videoId, event, videoUrl, title) => {
        if (videoId) {
            event.preventDefault();
            event.stopPropagation();
            trackEventByUrl(videoUrl, title);
            this.setState({modalOpen: true});
        }
    };

    render() {
        const {id, markComponentLoadedAs, handleLoadedComponents} = this.props;
        return (
            <InnerSplitItemQuery asyncMode query={GET_COMPONENT} variables={{ id }}>
                {({ loading, error, data }) => {
                    if (loading) return null;
                    if (error) {
                        markComponentLoadedAs(id, true);
                        return <div>{error}</div>;
                    }

                    const field = data.paragraphById;
                    const hasInnerContent = field.fieldTxtHeadline || field.fieldTxtSubTitle || field.fieldHtmlBody || field.fieldObjectSplitCta.length;
                    const hasTitle = field.fieldTxtHeadline || field.fieldTxtSubTitle;
                    const videoId = field.fieldVarIsVideoLink && field.fieldVarVideoLink && field.fieldVarVideoLink.uri ? ReactVideoModal.getVideoId(field.fieldVarVideoLink.uri) : null;
                    const videoImage = field.fieldVideoImage && field.fieldVideoImage.entity && field.fieldVideoImage.entity.fieldMediaImage;

                    let additionalClasses = '';
                    let backgroundContainerClasses = '';
                    let backgroundContainerInnerClasses = '';

                    if (field.fieldVarSubheadAboveHeadline) {
                        additionalClasses += ' splitContentInner--subheadlineAbove';
                    }

                    if (videoId) {
                        additionalClasses += ' splitContentInner--videoOnly';
                    }
                    if (videoId && videoImage && videoImage.url) {
                        additionalClasses += ' splitContentInner--videoImage';
                    }
                    else if (!hasInnerContent) {
                        additionalClasses += ' splitContentInner--imageOnly';
                        backgroundContainerClasses += ' splitContentInnerBackground--imageOnly';
                    }

                    if (hasTitle) {
                        additionalClasses += ' splitContentInner--hasTitle';
                    }

                    const imageAboveContent = field.fieldImageAboveContent;
                    const reverseVideoLinkText = field.fieldReverseVideoLinkText;

                    let videoContentClasses = 'innerCardHover__videoContent';
                    if (reverseVideoLinkText) {
                        videoContentClasses = `${videoContentClasses} innerCardHover__videoContent--inverted`
                    }

                    const styles = fieldObjectSectionStyleCss(field.fieldObjectSplitStyle, additionalClasses, imageAboveContent);

                    if (styles.childBackground.isActive) {
                        backgroundContainerClasses += ' splitContentInnerBackground';
                        backgroundContainerInnerClasses += ' splitContentInnerBackground__inner';
                    }


                    const overlayStyles = {};
                    if (videoId) {
                        if (styles.childBackground.isActive && styles.childBackground.css.backgroundColor && styles.childBackground.css.background) {
                            overlayStyles.backgroundColor = styles.childBackground.css.backgroundColor;
                        }
                        // Unset Z-Index if a Modal will be playing
                        styles.css.zIndex = 'unset';
                    }

                    markComponentLoadedAs(id, true);
                    this.hasRendered();

                    return (
                        <div className={`splitContentInner${styles.classNames}`} style={styles.css} id={styles.id}>

                            {styles.childBackground.isActive && (
                                <div className={backgroundContainerClasses}>
                                    <div>
                                        <div className="splitContentInnerBackground__image" style={styles.childBackground.css}/>
                                    </div>
                                </div>
                            )}

                            {videoId && (
                                <React.Fragment>
                                    <ReactVideoModal uri={field.fieldVarVideoLink.uri} isOpen={this.state.modalOpen} onClose={() => this.setState({modalOpen: false})}/>

                                    <div className={videoContentClasses}>
                                        {field.fieldTxtHeadline && field.fieldTxtHeadline.trim().length ? (
                                            <div className="innerCardHover__outerContent">
                                                <h2 dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtHeadline)}} />

                                            </div>
                                        ) : null}

                                        <span className="innerCardHover__ItemOverlay" style={overlayStyles} />

                                        <div className="innerCardHover__inner">
                                            {field.fieldTxtHeadline && (
                                                // eslint-disable-next-line react/no-danger
                                                <h3 className="innerCardHover__innerTitle" dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtHeadline)}} />
                                            )}

                                            {field.fieldHtmlBody && field.fieldHtmlBody.processed && (
                                                // eslint-disable-next-line react/no-danger
                                                <div className="innerCardHover__ItemBody" dangerouslySetInnerHTML={{__html: filterHTML(field.fieldHtmlBody.processed)}}/>
                                            )}

                                        </div>
                                    </div>
                                
                                    <div className="innerCardHover__videoImage">
                                        {videoId && videoImage && (
                                            <React.Fragment>
                                                <img src={filterResource(videoImage.url)} alt={videoImage.alt}/>
                                                <div className="innerCardHover__videoPlayButton">
                                                    <svg enableBackground="new 0 0 85 85" viewBox="0 0 85 85" xmlns="http://www.w3.org/2000/svg"><g fill="#fff"><path d="m42.5 0c-23.5 0-42.5 19-42.5 42.5s19 42.5 42.5 42.5 42.5-19 42.5-42.5-19-42.5-42.5-42.5zm0 77.7c-19.5 0-35.2-15.7-35.2-35.2s15.7-35.2 35.2-35.2 35.2 15.7 35.2 35.2-15.7 35.2-35.2 35.2z"/><path d="m32.1 61.6 31.5-19.1-31.5-19.1z"/></g></svg>
                                                </div>
                                            </React.Fragment>
                                        )}
                                        {(field.fieldTxtSubTitle && field.fieldTxtSubTitle.trim().length) ? (
                                            <div className="innerCardHover__videoOverlayContent">
                                                {field.fieldTxtSubTitle && field.fieldTxtSubTitle.trim().length ? (
                                                    <h3 className="innerCardHover__videoOverlayContentSubtitle" dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtSubTitle)}} />
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>


                                    {field.fieldHtmlBody && field.fieldHtmlBody.processed ? (
                                        <div className="innerCardHover__videoContentBody" dangerouslySetInnerHTML={{__html: filterHTML(field.fieldHtmlBody.processed)}}/>
                                    ) : null}

                                    <div className="innerCardHover" style={styles}>
                                        {field.fieldVarVideoLink && field.fieldVarVideoLink.uri && (
                                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                                            <button className="videoButton innerCardHover__ItemLink"
                                                    onClick={(e) => this.openModal(videoId, e, field.fieldVarVideoLink.uri, field.fieldTxtHeadline || 'Play Video')} />
                                        )}
                                    </div>
                                </React.Fragment>
                            )}

                            {(!videoId && hasInnerContent) ? (
                                <div className="splitContentInner__content">

                                    {imageAboveContent && styles.childBackground.url && (

                                        <div className="splitContentInner__topImage"
                                             style={{
                                                 background: `url("${styles.childBackground.url}") no-repeat center center`,
                                                 backgroundSize: 'cover'
                                             }}
                                        />
                                    )}

                                    <div className={`splitContentInner__innerContent${backgroundContainerInnerClasses}`}>

                                        <div className="splitContentInner__innerText">
                                            {!field.fieldVarSubheadAboveHeadline ? (
                                                <div>
                                                    {field.fieldTxtHeadline && (
                                                        // eslint-disable-next-line react/no-danger
                                                        <h2 dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtHeadline)}} />
                                                    )}

                                                    {field.fieldTxtSubTitle && (
                                                        // eslint-disable-next-line react/no-danger
                                                        <h3 dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtSubTitle)}} />
                                                    )}
                                                </div>
                                            ) : (
                                                <div>
                                                    {field.fieldTxtSubTitle && (
                                                        // eslint-disable-next-line react/no-danger
                                                        <h3 dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtSubTitle)}} />
                                                    )}

                                                    {field.fieldTxtHeadline && (
                                                        // eslint-disable-next-line react/no-danger
                                                        <h2 dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtHeadline)}} />
                                                    )}
                                                </div>
                                            )}

                                            {field.fieldHtmlBody && field.fieldHtmlBody.processed && (
                                                // eslint-disable-next-line react/no-danger
                                                <div className="splitContentInner__body">
                                                    {filterHTML(field.fieldHtmlBody.processed, true)}
                                                </div>
                                            )}
                                        </div>

                                        {field.fieldObjectSplitCta && field.fieldObjectSplitCta.map(component => (
                                            <Paragraph key={`${this.props.id}_${component.targetRevisionId}`}
                                                       id={String(component.targetRevisionId)}
                                                       handleLoadedComponents={handleLoadedComponents}/>
                                        ))}
                                    </div>

                                </div>
                            ) : null}

                        </div>
                    );
                }}
            </InnerSplitItemQuery>
        );
    }
}

export default InnerSplitItem;



// WEBPACK FOOTER //
// ./frontend/components/SplitContent/InnerSplitItem.jsx